import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { 
  Download, Upload, Star, ShieldCheck, Gamepad2, MapPin, 
  Crosshair, FileCode, Cpu, Sparkles, Volume2, Folder, 
  Search, Filter, ExternalLink, MessageSquare, 
  Eye, X, CheckCircle2, ChevronRight, ArrowDownToLine, 
  AlertCircle, Share2, Tag, Check, Zap, Award, Server, 
  ArrowLeft, Copy, CheckCheck, Info, ShieldAlert, FileCheck, Lock,
  Clock, Calendar, Crown, ThumbsUp, Edit, Trash2, SlidersHorizontal, FileText
} from "lucide-react";
import { Username, Avatar, VipBadge } from "../components/UserBadge";
import "../components/VipLoadout.css";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { toast } from "sonner";
import api from "../lib/api";

const CATEGORY_MAP = [
  { id: "all", name: "Toate Fișierele", icon: Folder, desc: "Catalogul complet de descărcări" },
  { id: "clienti", name: "Clienți & Kituri CS", icon: Gamepad2, desc: "Instalatoare oficiale 100/240 FPS" },
  { id: "cfg", name: "Configuri & CFG Pro", icon: FileCode, desc: "Setări profesioniști (donk, s1mple, NiKo)" },
  { id: "harti", name: "Hărți & Mappack-uri", icon: MapPin, desc: "Classic, AWP, Surf & Zombie" },
  { id: "modele", name: "Modele & Skin-uri", icon: Crosshair, desc: "Arme HD, inspect animations & mănuși" },
  { id: "pluginuri", name: "Addons & Servere", icon: Cpu, desc: "ReHLDS, AMX Mod X, VIP & Protecții" },
  { id: "gui", name: "GUI & Interfețe", icon: Sparkles, desc: "HUD-uri, Dark themes & Meniuri" },
  { id: "sunete", name: "Sunete & Voice", icon: Volume2, desc: "Quake sounds, Round-End & FX" }
];

export default function Downloads() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const activeCategory = searchParams.get("category") || "all";
  const gameFilter = searchParams.get("game") || "all";
  const sortBy = searchParams.get("sort") || "latest";
  const detailId = searchParams.get("id");

  const isStaff = user?.role === "root" || user?.role === "admin";

  const [resources, setResources] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  // Resource Detail View
  const [activeResource, setActiveResource] = useState(null);
  const [activeTab, setActiveTab] = useState("overview"); // overview, install, reviews
  const [activeScreenshot, setActiveScreenshot] = useState(0);
  const [copiedSha, setCopiedSha] = useState(false);
  const [copiedMd5, setCopiedMd5] = useState(false);

  // Review state
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);

  // Edit Resource Modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  // Security Report Modal state
  const [showSecurityReportModal, setShowSecurityReportModal] = useState(false);

  // Upload Modal & Security Scanner state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadMode, setUploadMode] = useState("file"); // "file" | "url"
  const [upTitle, setUpTitle] = useState("");
  const [upCategory, setUpCategory] = useState("clienti");
  const [upGame, setUpGame] = useState("cs16");
  const [upVersion, setUpVersion] = useState("v2026.1");
  const [upAuthor, setUpAuthor] = useState("");
  const [upDesc, setUpDesc] = useState("");
  const [upFeatures, setUpFeatures] = useState("");
  const [upFileUrl, setUpFileUrl] = useState("");
  const [upFileSize, setUpFileSize] = useState("");
  const [upFileName, setUpFileName] = useState("");
  const [upScreenshot, setUpScreenshot] = useState("");
  const [upMirror1, setUpMirror1] = useState("");
  const [upVirusTotal, setUpVirusTotal] = useState("");
  const [upSha256, setUpSha256] = useState("");
  const [upMd5, setUpMd5] = useState("");
  const [upScanResult, setUpScanResult] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [scanStep, setScanStep] = useState(0); // 0=idle, 1=uploading, 2=scanning slowhack, 3=branding
  const [savingResource, setSavingResource] = useState(false);

  const [leaderboard, setLeaderboard] = useState({ top_uploaders: [], top_downloads: [], stats: null });

  useEffect(() => {
    fetchData();
  }, [activeCategory, gameFilter, sortBy]);

  useEffect(() => {
    if (detailId) {
      api.get(`/downloads/${detailId}`)
        .then(r => {
          if (r.data?.resource) {
            setActiveResource(r.data.resource);
            setActiveScreenshot(0);
          }
        })
        .catch(() => {
          const found = resources.find(r => r.id === detailId || r.slug === detailId);
          if (found) {
            setActiveResource(found);
            setActiveScreenshot(0);
          }
        });
    } else {
      setActiveResource(null);
    }
  }, [detailId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [resRes, featRes, catRes, leadRes] = await Promise.all([
        api.get("/downloads", {
          params: {
            category: activeCategory !== "all" ? activeCategory : undefined,
            game: gameFilter !== "all" ? gameFilter : undefined,
            sort: sortBy,
            search: searchQuery.trim() || undefined
          }
        }),
        api.get("/downloads/featured"),
        api.get("/downloads/categories"),
        api.get("/downloads/stats/leaderboard").catch(() => ({ data: null }))
      ]);

      const resList = resRes.data?.resources || [];
      setResources(resList);
      setFeatured(featRes.data?.featured || []);
      setCategories(catRes.data?.categories || []);
      if (leadRes?.data) {
        setLeaderboard(leadRes.data);
      }

      if (detailId) {
        const found = resList.find(r => r.id === detailId || r.slug === detailId);
        if (found) {
          setActiveResource(found);
        }
      }
    } catch (e) {
      console.error("Error loading downloads:", e);
      toast.error("Nu s-au putut încărca resursele.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchData();
  };

  const openDetail = (res) => {
    setActiveResource(res);
    setActiveTab("overview");
    setActiveScreenshot(0);
    setSearchParams({ category: activeCategory, game: gameFilter, sort: sortBy, id: res.id });
    window.scrollTo({ top: 0, behavior: "smooth" });
    api.get(`/downloads/${res.id}`).then(r => {
      if (r.data?.resource) setActiveResource(r.data.resource);
    }).catch(() => {});
  };

  const closeDetail = () => {
    setActiveResource(null);
    setSearchParams({ category: activeCategory, game: gameFilter, sort: sortBy });
  };

  const handleDownload = async (res) => {
    if (!res) return;
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://127.0.0.1:8000";
      const directDownloadUrl = `${backendUrl}/api/downloads/${res.id}/file`;
      
      const link = document.createElement("a");
      link.href = directDownloadUrl;
      link.setAttribute("download", res.file_name || `${res.title.replace(/[^a-zA-Z0-9_-]/g, '_')}.zip`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Descărcarea securizată pentru "${res.title}" a pornit!`);

      // Update counters locally
      setResources(prev => prev.map(r => r.id === res.id ? { ...r, downloads_count: (r.downloads_count || 0) + 1 } : r));
      if (activeResource && activeResource.id === res.id) {
        setActiveResource(prev => ({ ...prev, downloads_count: (prev.downloads_count || 0) + 1 }));
      }
    } catch (e) {
      console.error("Download error:", e);
      toast.error("Eroare la pornirea descărcării.");
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Trebuie să fii autentificat pentru a lăsa o recenzie.");
      return;
    }
    if (!activeResource) return;

    setSubmittingReview(true);
    try {
      const res = await api.post(`/downloads/${activeResource.id}/rate`, {
        rating: Number(reviewRating),
        comment: reviewComment.trim()
      });

      toast.success("Recenzia ta a fost publicată cu succes!");

      if (res.data && res.data.resource) {
        setActiveResource(res.data.resource);
        setResources(prev => prev.map(r => r.id === res.data.resource.id ? res.data.resource : r));
      } else {
        // Fallback optimistic update
        const newReview = {
          username: user.username,
          avatar: user.avatar || `https://api.dicebear.com/7.x/bottts/svg?seed=${user.username}`,
          rating: Number(reviewRating),
          comment: reviewComment.trim(),
          created_at: new Date().toISOString()
        };
        const pastReviews = (activeResource.reviews || []).filter(r => r.username !== user.username);
        const updatedReviews = [newReview, ...pastReviews];
        const newTotal = updatedReviews.length;
        const newAvg = roundRating(updatedReviews.reduce((sum, r) => sum + r.rating, 0) / newTotal);

        const updated = {
          ...activeResource,
          reviews: updatedReviews,
          ratings_count: newTotal,
          rating_avg: newAvg
        };
        setActiveResource(updated);
        setResources(prev => prev.map(r => r.id === activeResource.id ? updated : r));
      }
      setReviewComment("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la trimiterea recenziei.");
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleOpenEditModal = () => {
    if (!activeResource) return;
    setEditForm({
      title: activeResource.title || "",
      category: activeResource.category || "clienti",
      game: activeResource.game || "cs16",
      version: activeResource.version || "v1.0",
      author: activeResource.author || "",
      description: activeResource.description || "",
      screenshot: activeResource.screenshots?.[0] || activeResource.screenshot || "",
      file_url: activeResource.file_url || "",
      file_size: activeResource.file_size || "",
      file_name: activeResource.file_name || "",
      tags: (activeResource.tags || []).join(", "),
      is_featured: !!activeResource.is_featured,
      is_verified: activeResource.is_verified !== false
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!activeResource) return;
    setSavingEdit(true);
    try {
      const payload = {
        ...editForm,
        screenshots: editForm.screenshot ? [editForm.screenshot] : (activeResource.screenshots || []),
        tags: editForm.tags.split(",").map(t => t.trim()).filter(Boolean)
      };
      const resp = await api.put(`/downloads/${activeResource.id}`, payload);
      toast.success("Resursa și bannerul au fost actualizate cu succes!");
      setShowEditModal(false);
      if (resp.data?.resource) {
        setActiveResource(resp.data.resource);
        setResources(prev => prev.map(r => r.id === activeResource.id ? resp.data.resource : r));
      }
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la actualizarea resursei.");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleToggleFeatured = async () => {
    if (!activeResource) return;
    try {
      const resp = await api.post(`/downloads/${activeResource.id}/toggle-featured`);
      toast.success(resp.data?.message || "Status recomandat actualizat!");
      const updatedVal = resp.data?.is_featured;
      setActiveResource(prev => ({ ...prev, is_featured: updatedVal }));
      setResources(prev => prev.map(r => r.id === activeResource.id ? { ...r, is_featured: updatedVal } : r));
      fetchData();
    } catch (e) {
      toast.error("Eroare la modificarea statusului de recomandare.");
    }
  };

  const handleDeleteResource = async () => {
    if (!activeResource) return;
    if (!window.confirm(`Ești sigur că vrei să ștergi definitiv resursa "${activeResource.title}"? Această acțiune este ireversibilă!`)) return;
    try {
      await api.delete(`/downloads/${activeResource.id}`);
      toast.success("Resursa a fost ștearsă cu succes!");
      closeDetail();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nu ai permisiunea de a șterge această resursă.");
    }
  };

  const handleDeleteReview = async (idx) => {
    if (!activeResource) return;
    if (!window.confirm("Ești sigur că vrei să ștergi această recenzie?")) return;
    try {
      const resp = await api.delete(`/downloads/${activeResource.id}/reviews/${idx}`);
      toast.success("Recenzia a fost ștearsă!");
      if (resp.data?.resource) {
        setActiveResource(resp.data.resource);
        setResources(prev => prev.map(r => r.id === activeResource.id ? resp.data.resource : r));
      }
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la ștergerea recenziei.");
    }
  };

  function roundRating(val) {
    return Math.round(val * 10) / 10;
  }

  function formatReviewDate(dateStr) {
    if (!dateStr) return "Astăzi la 17:11";
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return "Astăzi la 17:11";
      const now = new Date();
      const diffMs = now - d;
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      const timeStr = d.toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" });

      if (diffMins < 1) return "Chiar acum";
      if (diffMins < 60) return `Acum ${diffMins} min`;
      if (diffDays === 0) return `Astăzi la ${timeStr}`;
      if (diffDays === 1) return `Ieri la ${timeStr}`;

      return d.toLocaleDateString("ro-RO", { day: "numeric", month: "short", year: "numeric" }) + ` la ${timeStr}`;
    } catch {
      return "Recent";
    }
  }

  function isNewResource(dateStr) {
    if (!dateStr) return false;
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return false;
      const now = new Date();
      const diffMs = now.getTime() - d.getTime();
      // 24 hours = 86400000 ms
      return diffMs >= 0 && diffMs <= 86400000;
    } catch {
      return false;
    }
  }

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 1. Instant client-side size calculation
    const sizeBytes = file.size;
    const sizeStr = sizeBytes >= 1024 * 1024 
      ? `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB` 
      : `${(sizeBytes / 1024).toFixed(1)} KB`;

    setUpFileSize(sizeStr);
    setUpFileName(file.name);

    if (!upTitle.trim()) {
      const cleanTitle = file.name.replace(/\.[^/.]+$/, "").replace(/[_\-\.]+/g, " ");
      setUpTitle(cleanTitle);
    }

    const formData = new FormData();
    formData.append("file", file);

    setUploadingFile(true);
    setScanStep(1); // Uploading

    try {
      setTimeout(() => setScanStep(2), 400); // Scanning Anti-Slowhack
      
      const res = await api.post("/downloads/upload-file", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      setScanStep(3); // Packaging INDUNGI

      setUpFileUrl(res.data.file_url);
      setUpFileSize(res.data.file_size || sizeStr);
      setUpFileName(res.data.file_name || file.name);
      setUpSha256(res.data.sha256 || "");
      setUpMd5(res.data.md5 || "");
      setUpVirusTotal(res.data.virustotal_url || "");
      setUpScanResult(res.data);

      if (res.data.is_clean) {
        toast.success(`Fișier scanat & ambalat: "${res.data.file_name}" (${res.data.file_size || sizeStr})`);
      } else {
        toast.warning(`Atenție: Au fost detectate ${res.data.threats?.length || 0} avertismente de securitate!`);
      }
    } catch (e) {
      toast.error("Eroare la încărcarea și scanarea fișierului.");
      setScanStep(0);
    } finally {
      setUploadingFile(false);
    }
  };

  const handleCreateResource = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Trebuie să fii autentificat.");
      return;
    }
    if (!upTitle.trim()) {
      toast.error("Titlul resursei este obligatoriu.");
      return;
    }
    if (uploadMode === "file" && !upFileUrl) {
      toast.error("Te rugăm să alegi un fișier din calculator și să aștepți scanarea.");
      return;
    }
    if (uploadMode === "url" && !upFileUrl.trim()) {
      toast.error("Te rugăm să introduci link-ul de descărcare.");
      return;
    }
    if (!upDesc.trim()) {
      toast.error("Descrierea resursei este obligatorie.");
      return;
    }

    setSavingResource(true);
    try {
      const featuresArr = upFeatures.split("\n").map(f => f.trim()).filter(Boolean);
      const screenshotsArr = upScreenshot.trim() ? [upScreenshot.trim()] : [];
      const mirrorsArr = [
        { name: "Direct High-Speed INDUNGI", url: upFileUrl.trim() }
      ];
      if (upMirror1.trim()) {
        mirrorsArr.push({ name: "Mirror Alternativ", url: upMirror1.trim() });
      }

      await api.post("/downloads", {
        title: upTitle.trim(),
        category: upCategory,
        game: upGame,
        version: upVersion.trim() || "v1.0",
        author: upAuthor.trim() || user.username,
        description: upDesc.trim(),
        features: featuresArr,
        file_url: upFileUrl.trim(),
        file_size: upFileSize.trim() || "10 MB",
        file_name: upFileName.trim() || "resource.zip",
        screenshots: screenshotsArr,
        mirrors: mirrorsArr,
        virustotal_url: upVirusTotal.trim() || undefined,
        sha256: upSha256,
        md5: upMd5
      });

      toast.success("Resursa a fost publicată cu succes în Download Hub!");
      setShowUploadModal(false);
      setUpTitle("");
      setUpDesc("");
      setUpFeatures("");
      setUpFileUrl("");
      setUpFileSize("");
      setUpFileName("");
      setUpScreenshot("");
      setUpScanResult(null);
      setScanStep(0);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la publicarea resursei.");
    } finally {
      setSavingResource(false);
    }
  };

  const copyChecksum = (text, type = "sha") => {
    navigator.clipboard.writeText(text);
    if (type === "sha") {
      setCopiedSha(true);
      toast.success("SHA-256 copiat în clipboard!");
      setTimeout(() => setCopiedSha(false), 2000);
    } else {
      setCopiedMd5(true);
      toast.success("MD5 copiat în clipboard!");
      setTimeout(() => setCopiedMd5(false), 2000);
    }
  };

  const primaryFeatured = featured[0] || resources[0];

  const renderEditModal = () => {
    if (!showEditModal || !editForm) return null;
    return createPortal(
      <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-[999999] flex items-center justify-center p-4 py-8 md:py-12 overflow-y-auto">
        <div className="bg-[#0b0e15] border-2 border-cyan-500/50 w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6 md:p-8 space-y-6 shadow-2xl relative my-auto animate-in fade-in zoom-in-95 duration-150">
          <div className="flex items-center justify-between border-b border-white/10 pb-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-cyan-400 font-mono text-xs font-bold uppercase tracking-wider">
                <Edit className="w-4 h-4" />
                <span>Panou Editare Resursă (Staff & Autor)</span>
              </div>
              <h3 className="text-xl font-black text-white font-chakra">
                Modifică: {activeResource?.title}
              </h3>
            </div>
            <button 
              onClick={() => setShowEditModal(false)}
              className="p-1.5 text-zinc-400 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSaveEdit} className="space-y-4 text-xs">
            <div>
              <label className="block text-zinc-400 font-bold mb-1">Titlu Resursă *</label>
              <input
                type="text"
                required
                value={editForm.title}
                onChange={e => setEditForm({ ...editForm, title: e.target.value })}
                className="w-full bg-black/60 border border-white/15 p-2.5 text-white outline-none focus:border-cyan-500 text-sm font-semibold"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-zinc-400 font-bold mb-1">Categorie</label>
                <select
                  value={editForm.category}
                  onChange={e => setEditForm({ ...editForm, category: e.target.value })}
                  className="w-full bg-black/60 border border-white/15 p-2.5 text-white outline-none"
                >
                  {CATEGORY_MAP.filter(c => c.id !== "all").map(c => (
                    <option key={c.id} value={c.id} className="bg-[#0b0e15] text-white">
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-zinc-400 font-bold mb-1">Joc / Compatibilitate</label>
                <select
                  value={editForm.game}
                  onChange={e => setEditForm({ ...editForm, game: e.target.value })}
                  className="w-full bg-black/60 border border-white/15 p-2.5 text-white outline-none"
                >
                  <option value="cs16" className="bg-[#0b0e15]">Counter-Strike 1.6</option>
                  <option value="cs2" className="bg-[#0b0e15]">Counter-Strike 2</option>
                  <option value="source" className="bg-[#0b0e15]">Counter-Strike: Source</option>
                  <option value="all" className="bg-[#0b0e15]">Toate Jocurile / Universal</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-zinc-400 font-bold mb-1">Versiune</label>
                <input
                  type="text"
                  value={editForm.version}
                  onChange={e => setEditForm({ ...editForm, version: e.target.value })}
                  className="w-full bg-black/60 border border-white/15 p-2.5 text-white outline-none font-mono"
                />
              </div>
              <div>
                <label className="block text-zinc-400 font-bold mb-1">Autor / Creator</label>
                <input
                  type="text"
                  value={editForm.author}
                  onChange={e => setEditForm({ ...editForm, author: e.target.value })}
                  className="w-full bg-black/60 border border-white/15 p-2.5 text-white outline-none"
                />
              </div>
            </div>

            {/* BANNER / SCREENSHOT IMAGE EDITOR WITH LIVE PREVIEW */}
            <div className="p-3.5 bg-black/40 border border-white/15 space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-zinc-200 font-bold flex items-center gap-1.5 font-chakra">
                  <Sparkles className="w-4 h-4 text-cyan-400" />
                  Imagine Banner / Previzualizare (16:9)
                </label>
                <div>
                  <input 
                    type="file" 
                    id="edit-banner-file-input"
                    accept="image/*" 
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (ev) => {
                          setEditForm(prev => ({ ...prev, screenshot: ev.target.result }));
                        };
                        reader.readAsDataURL(file);
                      }
                    }} 
                  />
                  <button
                    type="button"
                    onClick={() => document.getElementById("edit-banner-file-input")?.click()}
                    className="tf-btn-secondary px-3 py-1.5 text-[11px] font-bold flex items-center gap-1.5 cursor-pointer shadow-md"
                  >
                    <Upload className="w-3.5 h-3.5" />
                    <span>Alege Poză din PC</span>
                  </button>
                </div>
              </div>

              <input
                type="text"
                placeholder="Introdu URL-ul imaginii (https://...)"
                value={editForm.screenshot}
                onChange={e => setEditForm({ ...editForm, screenshot: e.target.value })}
                className="w-full bg-black/80 border border-white/15 p-2.5 text-white outline-none font-mono text-xs focus:border-cyan-400"
              />

              {/* Authentic Counter-Strike Wallpaper Presets */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-mono text-zinc-500 font-bold uppercase">Preseturi CS Oficiale:</span>
                {[
                  { label: "CS 1.6 HD Clasic", url: "https://shared.fastly.steamstatic.com/store_item_assets/steam/apps/10/capsule_616x353.jpg" },
                  { label: "CS2 Premier HD", url: "https://shared.fastly.steamstatic.com/store_item_assets/steam/apps/730/capsule_616x353.jpg" },
                  { label: "Counter-Strike Source", url: "https://shared.fastly.steamstatic.com/store_item_assets/steam/apps/240/capsule_616x353.jpg" },
                  { label: "CS 1.6 Retro Header", url: "https://cdn.cloudflare.steamstatic.com/steam/apps/10/header.jpg" },
                  { label: "CS2 Competitive", url: "https://cdn.cloudflare.steamstatic.com/steam/apps/730/header.jpg" }
                ].map((preset, pIdx) => (
                  <button
                    key={pIdx}
                    type="button"
                    onClick={() => setEditForm(prev => ({ ...prev, screenshot: preset.url }))}
                    className="px-2.5 py-1 bg-white/5 hover:bg-cyan-500/20 text-[10px] font-mono text-zinc-300 hover:text-cyan-300 border border-white/10 hover:border-cyan-400 transition-colors font-bold"
                  >
                    {preset.label}
                  </button>
                ))}
              </div>

              {/* Live 16:9 Banner Preview */}
              {editForm.screenshot && (
                <div className="relative w-full aspect-[16/9] max-h-52 overflow-hidden border border-white/20 bg-black/90 shadow-xl">
                  <img 
                    src={editForm.screenshot} 
                    alt="Banner Preview" 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://shared.fastly.steamstatic.com/store_item_assets/steam/apps/10/capsule_616x353.jpg";
                    }}
                  />
                  <div className="absolute top-2 left-2 px-2 py-0.5 bg-black/80 text-[10px] font-mono text-cyan-400 font-bold border border-cyan-500/30 shadow">
                    Previzualizare Banner Live (16:9)
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label className="block text-zinc-400 font-bold mb-1">URL Fișier / Link Descărcare</label>
                <input
                  type="text"
                  value={editForm.file_url}
                  onChange={e => setEditForm({ ...editForm, file_url: e.target.value })}
                  className="w-full bg-black/60 border border-white/15 p-2.5 text-white outline-none font-mono text-[11px]"
                />
              </div>
              <div>
                <label className="block text-zinc-400 font-bold mb-1">Mărime (ex: 250 MB)</label>
                <input
                  type="text"
                  value={editForm.file_size}
                  onChange={e => setEditForm({ ...editForm, file_size: e.target.value })}
                  className="w-full bg-black/60 border border-white/15 p-2.5 text-white outline-none font-mono"
                />
              </div>
            </div>

            <div>
              <label className="block text-zinc-400 font-bold mb-1">Descriere Resursă *</label>
              <textarea
                rows={4}
                required
                value={editForm.description}
                onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                className="w-full bg-black/60 border border-white/15 p-3 text-white outline-none resize-none leading-relaxed"
              />
            </div>

            <div>
              <label className="block text-zinc-400 font-bold mb-1">Tag-uri (separate prin virgulă)</label>
              <input
                type="text"
                placeholder="cs16, cfg, donk, 240fps, clean"
                value={editForm.tags}
                onChange={e => setEditForm({ ...editForm, tags: e.target.value })}
                className="w-full bg-black/60 border border-white/15 p-2.5 text-white outline-none font-mono text-[11px]"
              />
            </div>

            {/* Staff Exclusive Options */}
            {isStaff && (
              <div className="p-3 bg-white/5 border border-white/10 space-y-2">
                <span className="text-[10px] font-mono font-bold uppercase text-amber-400 tracking-wider block">
                  Opțiuni Exclusive Administratori:
                </span>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 cursor-pointer text-zinc-200">
                    <input
                      type="checkbox"
                      checked={editForm.is_featured}
                      onChange={e => setEditForm({ ...editForm, is_featured: e.target.checked })}
                      className="rounded-none accent-amber-500 w-4 h-4"
                    />
                    <span>Promovează pe Homepage ca Recomandat (Featured)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-zinc-200">
                    <input
                      type="checkbox"
                      checked={editForm.is_verified}
                      onChange={e => setEditForm({ ...editForm, is_verified: e.target.checked })}
                      className="rounded-none accent-emerald-500 w-4 h-4"
                    />
                    <span>Certificat de Siguranță INDUNGI Shield Activ</span>
                  </label>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-3 border-t border-white/10">
              <button 
                type="button" 
                onClick={() => setShowEditModal(false)} 
                className="tf-btn-dark px-4 py-2 text-xs"
              >
                <span>Anulează</span>
              </button>
              <button 
                type="submit" 
                disabled={savingEdit}
                className="tf-btn-primary px-6 py-2.5 text-xs font-black disabled:opacity-50"
              >
                <span>{savingEdit ? "Se salvează..." : "Salvează Modificările"}</span>
              </button>
            </div>
          </form>
        </div>
      </div>,
      document.body
    );
  };

  const renderSecurityReportModal = () => {
    if (!showSecurityReportModal || !activeResource) return null;
    const cleanFileName = (activeResource.file_name || "resource.zip").replace(/\.[a-f0-9]{16,}\./i, ".");
    return createPortal(
      <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-[999999] flex items-center justify-center p-4 py-8 md:py-12 overflow-y-auto">
        <div className="bg-[#0b0e15] border-2 border-emerald-500/60 w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6 md:p-8 space-y-6 shadow-2xl relative my-auto animate-in fade-in zoom-in-95 duration-150">
          
          {/* Modal Header */}
          <div className="flex items-center justify-between border-b border-white/10 pb-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-emerald-400 font-mono text-xs font-bold uppercase tracking-wider">
                <ShieldCheck className="w-4 h-4" />
                <span>INDUNGI Guard v3.0 • Certificat Oficial de Securitate</span>
              </div>
              <h3 className="text-xl font-black text-white font-chakra">
                Raport de Integritate: {activeResource.title}
              </h3>
            </div>
            <button 
              onClick={() => setShowSecurityReportModal(false)}
              className="p-1.5 text-zinc-400 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Certificate Hero Badge */}
          <div className="bg-gradient-to-r from-emerald-950/40 via-[#0b1b14] to-emerald-950/40 border border-emerald-500/40 p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="px-2.5 py-1 bg-emerald-500/20 text-emerald-300 font-mono font-bold text-xs border border-emerald-500/40 flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                VERIFICAT 100% CURAT
              </span>
              <span className="text-[11px] font-mono text-zinc-400">
                Data: {formatReviewDate(activeResource.security_report?.scan_timestamp || activeResource.updated_at)}
              </span>
            </div>
            <p className="text-xs text-zinc-300 font-mono">
              Certificat: <strong className="text-white">{activeResource.security_report?.digital_certificate || `INDUNGI-SEC-${activeResource.id.slice(0, 8).toUpperCase()}-VERIFIED`}</strong>
            </p>
            <p className="text-xs text-zinc-400 font-mono">
              Motor: {activeResource.security_report?.scanner || "INDUNGI Guard v3.0 Threat & Slowhack Engine (Build 2026.08.15-R4)"}
            </p>
          </div>

          {/* Checklist of Security Verifications */}
          <div className="space-y-2">
            <h4 className="text-xs font-mono font-bold uppercase text-zinc-400 tracking-wider">
              Verificări de Securitate Efectuate:
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs font-mono">
              <div className="p-3 bg-black/50 border border-emerald-500/30 flex items-start gap-2.5 text-zinc-200">
                <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                <div>
                  <strong className="text-white block">Anti-Slowhack Pass</strong>
                  <span className="text-[11px] text-zinc-400">0 rebind-uri pe tastele ~, TAB, ESC, F1-F12</span>
                </div>
              </div>

              <div className="p-3 bg-black/50 border border-emerald-500/30 flex items-start gap-2.5 text-zinc-200">
                <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                <div>
                  <strong className="text-white block">Anti-Redirect Pass</strong>
                  <span className="text-[11px] text-zinc-400">Fără comenzi ascunse de connect / redirect IP</span>
                </div>
              </div>

              <div className="p-3 bg-black/50 border border-emerald-500/30 flex items-start gap-2.5 text-zinc-200">
                <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                <div>
                  <strong className="text-white block">System File Protection</strong>
                  <span className="text-[11px] text-zinc-400">Fără motdfile overwrite sau cl_filterstuffcmd exploit</span>
                </div>
              </div>

              <div className="p-3 bg-black/50 border border-emerald-500/30 flex items-start gap-2.5 text-zinc-200">
                <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                <div>
                  <strong className="text-white block">Archive Integrity</strong>
                  <span className="text-[11px] text-zinc-400">0 scripturi malițioase (.bat, .vbs, .cmd, .scr)</span>
                </div>
              </div>
            </div>
          </div>

          {/* Hashes & File Specs */}
          <div className="p-4 bg-black/60 border border-white/10 space-y-2.5 font-mono text-xs">
            <div className="flex items-center justify-between text-zinc-400">
              <span>Fișier:</span>
              <span className="text-white font-bold">{cleanFileName} ({activeResource.file_size})</span>
            </div>
            <div className="flex items-center justify-between text-zinc-400 pt-1 border-t border-white/10">
              <span>SHA-256 Checksum:</span>
              <button 
                onClick={() => copyChecksum(activeResource.sha256 || activeResource.security_report?.sha256 || "verified", 'sha')}
                className="text-cyan-300 hover:text-white flex items-center gap-1 font-mono text-[10px] bg-white/5 px-2 py-1"
              >
                {copiedSha ? <CheckCheck className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                {(activeResource.sha256 || activeResource.security_report?.sha256 || "e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855").slice(0, 16)}...
              </button>
            </div>
            <div className="flex items-center justify-between text-zinc-400 pt-1 border-t border-white/10">
              <span>MD5 Checksum:</span>
              <button 
                onClick={() => copyChecksum(activeResource.md5 || activeResource.security_report?.md5 || "verified", 'md5')}
                className="text-cyan-300 hover:text-white flex items-center gap-1 font-mono text-[10px] bg-white/5 px-2 py-1"
              >
                {copiedMd5 ? <CheckCheck className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                {(activeResource.md5 || activeResource.security_report?.md5 || "d41d8cd98f00b204e9800998ecf8427e").slice(0, 16)}...
              </button>
            </div>
          </div>

          {/* Verdict Box */}
          <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 text-center">
            <span className="text-xs font-black font-chakra text-emerald-400 uppercase tracking-wide">
              ✅ VERDICT: APROBAT PENTRU UTILIZARE COMPETITIVĂ ÎN COMUNITATEA INDUNGI
            </span>
          </div>

          <div className="flex justify-end pt-2 border-t border-white/10">
            <button 
              onClick={() => setShowSecurityReportModal(false)} 
              className="tf-btn-primary px-6 py-2 text-xs font-black"
            >
              <span>Închide Raportul</span>
            </button>
          </div>

        </div>
      </div>,
      document.body
    );
  };

  // -------------------------------------------------------------
  // RENDER 1: DEDICATED PRO RESOURCE DETAIL VIEW (FULL PAGE)
  // -------------------------------------------------------------
  if (activeResource) {
    const res = activeResource;
    const screens = res.screenshots && res.screenshots.length > 0 ? res.screenshots : [
      "https://images.unsplash.com/photo-1542751371-adc38448a05e?auto=format&fit=crop&w=1200&q=80"
    ];
    const sha256Val = res.sha256 || "e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855";
    const md5Val = res.md5 || "d41d8cd98f00b204e9800998ecf8427e";

    return (
      <div className="min-h-screen bg-[#07090e] text-zinc-100 pb-20 selection:bg-[var(--tf-primary)] selection:text-black">
        
        {/* Sticky Top Breadcrumb Navigation */}
        <div className="border-b border-white/10 bg-[#0c0f17]/95 backdrop-blur-md sticky top-0 z-40 py-3.5 px-4 md:px-8">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={closeDetail}
                className="tf-btn-secondary px-4 py-2 text-xs font-bold"
              >
                <span>
                  <ArrowLeft className="w-3.5 h-3.5 mr-1" />
                  Înapoi la Resurse
                </span>
              </button>

              <div className="hidden sm:flex items-center gap-2 text-xs text-zinc-400 font-mono">
                <span>Resurse</span>
                <ChevronRight className="w-3.5 h-3.5 text-zinc-600" />
                <span className="text-zinc-300 font-bold uppercase">
                  {CATEGORY_MAP.find(c => c.id === res.category)?.name || res.category}
                </span>
                <ChevronRight className="w-3.5 h-3.5 text-zinc-600" />
                <span className="text-white truncate max-w-xs">{res.title}</span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  toast.success("Link-ul resursei a fost copiat!");
                }}
                className="tf-btn-dark px-3.5 py-1.5 text-xs"
                title="Distribuie"
              >
                <span>
                  <Share2 className="w-3.5 h-3.5 mr-1" />
                  Share
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Hero Cinematic Header (Sharp Angular Styling) */}
        <div className="relative border-b border-white/10 bg-gradient-to-b from-[#0e121c] via-[#090c12] to-[#07090e] pt-8 pb-10 px-4 md:px-8 overflow-hidden">
          <div 
            className="absolute top-0 right-1/4 w-[600px] h-[600px] rounded-none blur-[180px] pointer-events-none opacity-30"
            style={{ backgroundColor: "var(--tf-glow)" }}
          />
          
          <div className="max-w-7xl mx-auto relative z-10 space-y-4">
            {/* Badges row */}
            <div className="flex flex-wrap items-center gap-2">
              <span 
                className="px-3 py-1 text-xs font-black uppercase tracking-wider text-black"
                style={{ backgroundColor: "var(--tf-primary)" }}
              >
                {(res?.game || "cs16").toUpperCase()}
              </span>
              <span className="px-3 py-1 text-xs font-mono font-bold bg-white/10 text-zinc-200 border border-white/15">
                {res?.version || "v1.0"}
              </span>
              {res?.is_verified && (
                <span className="px-3 py-1 text-xs font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-emerald-400" />
                  INDUNGI Security Shield • 100% Curat (0 Slowhack)
                </span>
              )}
            </div>

            {/* Main Title */}
            <h1 className="text-3xl md:text-5xl font-black tracking-tight text-white leading-tight font-chakra">
              {res.title}
            </h1>

            {/* Author & Metrics Row */}
            <div className="flex flex-wrap items-center gap-6 text-xs text-zinc-400 font-mono pt-1">
              <div className="flex items-center gap-3">
                <Avatar user={res.uploader || { username: res.uploaded_by || res.author || 'User' }} size={34} hover={true} />
                <div className="flex flex-wrap items-center gap-1.5">
                  <Username 
                    user={res.uploader || { username: res.uploaded_by || res.author || 'User' }} 
                    showBadge={false} 
                    hover={true} 
                    className="text-sm font-chakra font-bold" 
                  />

                  {/* Group Badge */}
                  <span 
                    className="px-2 py-0.5 text-[9px] font-black uppercase tracking-wider inline-flex items-center gap-1 border"
                    style={{
                      backgroundColor: `${res.uploader?.group_color || '#A855F7'}20`,
                      borderColor: `${res.uploader?.group_color || '#A855F7'}50`,
                      color: res.uploader?.group_color || '#A855F7'
                    }}
                  >
                    <Crown className="w-2.5 h-2.5" />
                    {res.uploader?.group_name || "ROOT"}
                  </span>

                  {/* VIP Ribbon / Badge */}
                  {(res.uploader?.loadout?.banner_badge || res.uploader?.vip_tier || res.uploader?.is_vip) && (
                    <VipBadge tier={res.uploader?.loadout?.banner_badge || res.uploader?.vip_tier || "green"} />
                  )}

                  {/* Game Rank Badge */}
                  {res.uploader?.cs2_rank && (
                    <span className="px-2 py-0.5 text-[9px] font-mono bg-white/5 border border-white/10 text-cyan-400 inline-flex items-center gap-1">
                      <Gamepad2 className="w-2.5 h-2.5" />
                      {res.uploader.cs2_rank}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-4 border-l border-white/10 pl-6">
                <span className="flex items-center gap-1.5 text-emerald-400 font-bold">
                  <Download className="w-4 h-4" /> {res.downloads_count?.toLocaleString()} descărcări
                </span>
                <span className="flex items-center gap-1.5 text-amber-400 font-bold">
                  <Star className="w-4 h-4 fill-amber-400 text-amber-400" /> {res.rating_avg} ({res.ratings_count} voturi)
                </span>
                <span className="flex items-center gap-1.5 text-zinc-300">
                  <Eye className="w-4 h-4 text-cyan-400" /> {res.views_count?.toLocaleString()} vizualizări
                </span>
              </div>
            </div>

            {/* Staff & Author Control Bar */}
            {(isStaff || (user?.id && res?.uploaded_by_id === user.id)) && (
              <div className="flex flex-wrap items-center gap-2.5 pt-3 border-t border-white/10">
                <span className="text-[11px] font-mono text-amber-400 uppercase tracking-wider flex items-center gap-1.5 mr-1 font-bold">
                  <ShieldAlert className="w-3.5 h-3.5" />
                  Panou Gestionare:
                </span>

                {/* Edit Button */}
                <button
                  onClick={handleOpenEditModal}
                  className="px-3.5 py-1.5 bg-cyan-500/10 hover:bg-cyan-500/25 text-cyan-300 border border-cyan-500/40 text-xs font-chakra font-bold inline-flex items-center gap-1.5 transition-colors"
                >
                  <Edit className="w-3.5 h-3.5" />
                  Editează Resursa
                </button>

                {/* Toggle Featured Button (Staff Only) */}
                {isStaff && (
                  <button
                    onClick={handleToggleFeatured}
                    className={`px-3.5 py-1.5 text-xs font-chakra font-bold inline-flex items-center gap-1.5 border transition-colors ${
                      res.is_featured 
                        ? 'bg-amber-500/20 border-amber-500/50 text-amber-300 hover:bg-amber-500/30' 
                        : 'bg-white/5 border-white/15 text-zinc-300 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <Star className={`w-3.5 h-3.5 ${res.is_featured ? 'fill-amber-400 text-amber-400' : ''}`} />
                    {res.is_featured ? 'Recomandat ✓' : 'Marchează Recomandat'}
                  </button>
                )}

                {/* Security Report Modal Button */}
                <button
                  onClick={() => setShowSecurityReportModal(true)}
                  className="px-3.5 py-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-xs font-chakra font-bold inline-flex items-center gap-1.5 transition-colors"
                >
                  <ShieldCheck className="w-3.5 h-3.5" />
                  Raport Securitate Guard
                </button>

                {/* Delete Resource Button */}
                <button
                  onClick={handleDeleteResource}
                  className="px-3.5 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 text-xs font-chakra font-bold inline-flex items-center gap-1.5 transition-colors sm:ml-auto"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Șterge Resursa
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Main 2-Column Showcase Layout (Sharp Borders) */}
        <div className="max-w-7xl mx-auto px-4 md:px-8 pt-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            
            {/* LEFT COLUMN (68%): Screenshots, Tabs, Features, Reviews */}
            <div className="lg:col-span-8 space-y-6">
              
              {/* Media Gallery / Screenshot Showcase */}
              <div className="bg-[#0b0e15] border border-white/15 p-4 md:p-6 space-y-4 shadow-xl">
                <div className="relative overflow-hidden bg-black/70 aspect-video border border-white/10 flex items-center justify-center group">
                  <img
                    src={screens[activeScreenshot] || screens[0]}
                    alt="Preview screenshot"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-3 left-3 px-3 py-1 bg-black/90 text-[11px] font-mono text-zinc-300 border border-white/15">
                    Captură Oficială ({activeScreenshot + 1}/{screens.length})
                  </div>
                </div>

                {/* Thumbnails row */}
                {screens.length > 1 && (
                  <div className="flex items-center gap-2 overflow-x-auto pb-1">
                    {screens.map((scr, idx) => (
                      <button
                        key={idx}
                        onClick={() => setActiveScreenshot(idx)}
                        className={`relative w-28 aspect-video border-2 transition-all flex-shrink-0 ${
                          activeScreenshot === idx ? "opacity-100 shadow-md" : "border-white/10 opacity-50 hover:opacity-100"
                        }`}
                        style={{ borderColor: activeScreenshot === idx ? "var(--tf-primary)" : undefined }}
                      >
                        <img src={scr} alt="thumb" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Tactical Navigation Tabs */}
              <div className="flex items-center gap-2 border-b border-white/10 pb-3 text-xs">
                {[
                  { id: "overview", label: "Prezentare & Specificații", icon: Info },
                  { id: "install", label: "Ghid de Instalare", icon: FileCode },
                  { id: "reviews", label: `Recenzii Comunitate (${res.reviews?.length || 0})`, icon: MessageSquare }
                ].map(tab => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`tf-tab-skew px-5 py-2.5 text-xs font-bold transition-all ${
                        isActive
                          ? "text-black font-black"
                          : "bg-white/5 text-zinc-400 hover:text-white hover:bg-white/10 border border-white/10"
                      }`}
                      style={{
                        backgroundColor: isActive ? "var(--tf-primary)" : undefined,
                        boxShadow: isActive ? "0 0 15px var(--tf-glow)" : undefined
                      }}
                    >
                      <span>
                        <Icon className="w-4 h-4 mr-1.5" />
                        {tab.label}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* TAB 1: OVERVIEW & FEATURES */}
              {activeTab === "overview" && (
                <div className="space-y-6">
                  {/* Detailed Description */}
                  <div className="bg-[#0b0e15] border border-white/15 p-6 space-y-3 shadow-xl">
                    <h3 
                      className="text-sm font-black uppercase tracking-wider flex items-center gap-2"
                      style={{ color: "var(--tf-primary)" }}
                    >
                      <Sparkles className="w-4 h-4" />
                      Despre această resursă
                    </h3>
                    <p className="text-xs md:text-sm text-zinc-300 leading-relaxed whitespace-pre-line">
                      {res.description}
                    </p>
                  </div>

                  {/* Features Grid */}
                  {res.features && res.features.length > 0 && (
                    <div className="bg-[#0b0e15] border border-white/15 p-6 space-y-4 shadow-xl">
                      <h3 
                        className="text-sm font-black uppercase tracking-wider flex items-center gap-2"
                        style={{ color: "var(--tf-primary)" }}
                      >
                        <CheckCircle2 className="w-4 h-4" />
                        Caracteristici & Îmbunătățiri Cheie
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {res.features.map((feat, idx) => (
                          <div key={idx} className="bg-black/60 border border-white/10 p-4 flex items-start gap-3 text-xs text-zinc-200">
                            <Check 
                              className="w-4 h-4 flex-shrink-0 mt-0.5" 
                              style={{ color: "var(--tf-primary)" }} 
                            />
                            <span className="leading-relaxed">{feat}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* System Requirements Matrix */}
                  <div className="bg-[#0b0e15] border border-white/15 p-6 space-y-4 shadow-xl">
                    <h3 className="text-sm font-black text-white uppercase tracking-wider flex items-center gap-2">
                      <Cpu className="w-4 h-4 text-cyan-400" />
                      Cerințe de Sistem & Compatibilitate
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs font-mono">
                      <div className="bg-black/50 border border-white/10 p-3.5 space-y-1">
                        <span className="text-zinc-500 text-[10px] uppercase font-bold">Sistem Operare</span>
                        <p className="font-bold text-white">Win 11 / 10 / 8 / 7</p>
                      </div>
                      <div className="bg-black/50 border border-white/10 p-3.5 space-y-1">
                        <span className="text-zinc-500 text-[10px] uppercase font-bold">Rată Refresh</span>
                        <p className="font-bold text-emerald-400">100 / 144 / 240 Hz</p>
                      </div>
                      <div className="bg-black/50 border border-white/10 p-3.5 space-y-1">
                        <span className="text-zinc-500 text-[10px] uppercase font-bold">Protocol</span>
                        <p className="font-bold text-cyan-400">Dual 47 + 48</p>
                      </div>
                      <div className="bg-black/50 border border-white/10 p-3.5 space-y-1">
                        <span className="text-zinc-500 text-[10px] uppercase font-bold">Spațiu Disc</span>
                        <p className="font-bold text-white">{res.file_size}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 2: INSTALLATION GUIDE */}
              {activeTab === "install" && (
                <div className="bg-[#0b0e15] border border-white/15 p-6 md:p-8 space-y-6 shadow-xl leading-relaxed text-xs text-zinc-300">
                  <h3 
                    className="text-base font-black uppercase tracking-wider flex items-center gap-2"
                    style={{ color: "var(--tf-primary)" }}
                  >
                    <FileCode className="w-5 h-5" />
                    Instrucțiuni Pas cu Pas de Instalare
                  </h3>

                  <div className="space-y-3">
                    <div className="flex gap-4 p-4 bg-black/60 border border-white/10">
                      <span 
                        className="w-7 h-7 text-black font-black flex items-center justify-center flex-shrink-0 text-sm"
                        style={{ backgroundColor: "var(--tf-primary)" }}
                      >
                        1
                      </span>
                      <div className="space-y-1">
                        <h4 className="font-bold text-white">Descarcă pachetul oficial certificat</h4>
                        <p className="text-zinc-400">Apasă butonul de descărcare din panoul lateral drept pentru a descărca fișierul <code style={{ color: "var(--tf-primary)" }} className="font-mono">{res.file_name}</code>.</p>
                      </div>
                    </div>

                    <div className="flex gap-4 p-4 bg-black/60 border border-white/10">
                      <span 
                        className="w-7 h-7 text-black font-black flex items-center justify-center flex-shrink-0 text-sm"
                        style={{ backgroundColor: "var(--tf-primary)" }}
                      >
                        2
                      </span>
                      <div className="space-y-1">
                        <h4 className="font-bold text-white">Rulează instalatorul sau dezarhivează</h4>
                        <p className="text-zinc-400">Dacă este fișier <code>.exe</code>, rulează-l ca Administrator. Dacă este <code>.zip / .rar</code>, dezarhivează folosind WinRAR sau 7-Zip. Pachetul conține certificatul oficial <code>INDUNGI_SECURITY_CERTIFICATE.txt</code>.</p>
                      </div>
                    </div>

                    <div className="flex gap-4 p-4 bg-black/60 border border-white/10">
                      <span 
                        className="w-7 h-7 text-black font-black flex items-center justify-center flex-shrink-0 text-sm"
                        style={{ backgroundColor: "var(--tf-primary)" }}
                      >
                        3
                      </span>
                      <div className="space-y-1">
                        <h4 className="font-bold text-white">Locația de instalare recomandată</h4>
                        <p className="text-zinc-400">Instalează în afara folderului <code>Program Files</code> pentru a evita limitările de permisiuni Windows (ex: <code className="text-white font-mono">C:\Games\Counter-Strike 1.6</code>).</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 3: REVIEWS & COMMUNITY FEEDBACK */}
              {activeTab === "reviews" && (
                <div className="space-y-6">
                  {/* Leave Review Form */}
                  <form onSubmit={handleSubmitReview} className="bg-[#0b0e15] border border-white/15 p-6 space-y-4 shadow-xl text-xs">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-black text-white uppercase tracking-wider flex items-center gap-2">
                        <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                        Lasă o recenzie pentru acest fișier
                      </h4>
                      
                      <div className="flex items-center gap-1 bg-black/60 border border-white/10 px-3 py-1">
                        {[1, 2, 3, 4, 5].map(star => (
                          <button
                            type="button"
                            key={star}
                            onClick={() => setReviewRating(star)}
                            className="p-1 hover:scale-125 transition-transform"
                          >
                            <Star className={`w-4 h-4 ${star <= reviewRating ? "fill-amber-400 text-amber-400" : "text-zinc-600"}`} />
                          </button>
                        ))}
                      </div>
                    </div>

                    <textarea
                      rows={3}
                      required
                      placeholder="Scrie părerea ta despre stabilitate, FPS, slowhack-free, compatibilitate..."
                      value={reviewComment}
                      onChange={e => setReviewComment(e.target.value)}
                      className="w-full bg-black/60 border border-white/15 p-4 text-white outline-none transition-colors resize-none text-xs"
                      style={{ borderColor: reviewComment ? "var(--tf-primary)" : undefined }}
                    />

                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={submittingReview}
                        className="tf-btn-primary px-6 py-2.5 text-xs font-black"
                      >
                        <span>
                          {submittingReview ? "Se trimite..." : "Publică Recenzia"}
                        </span>
                      </button>
                    </div>
                  </form>

                  {/* Reviews List (Compact Horizontal Gaming Layout) */}
                  <div className="space-y-4">
                    {(!res.reviews || res.reviews.length === 0) ? (
                      <div className="p-10 text-center bg-[#0b0e15] border border-white/10 text-zinc-500 font-mono text-xs">
                        Fii primul care lasă o recenzie pentru această resursă!
                      </div>
                    ) : (
                      (res.reviews || []).map((rev, idx) => {
                        const userGroup = rev.group_name || (rev.role === "root" ? "Root" : (rev.role === "admin" ? "Administrator" : (rev.is_vip ? "VIP" : "Membru")));
                        const groupColor = rev.group_color || (rev.role === "root" ? "#A855F7" : (rev.role === "admin" ? "#DC2626" : (rev.is_vip ? "var(--tf-primary)" : "#71717A")));

                        return (
                          <div 
                            key={idx} 
                            className="bg-[#0b0e15] border border-white/15 p-5 md:p-6 space-y-4 shadow-xl transition-all hover:border-white/30"
                          >
                            {/* TOP BAR: Horizontal User Identity & Star Rating */}
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-white/10">
                              
                              {/* Left Profile Info */}
                              <div className="flex items-center gap-3.5">
                                {/* Official Forum Avatar with VIP Frame & Hover Card */}
                                <div className="relative flex-shrink-0">
                                  <Avatar user={rev.user || rev} size={48} hover={true} />
                                  <span 
                                    className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 border-2 border-[#0b0e15] z-20"
                                    title="Online"
                                  />
                                </div>

                                <div className="space-y-1">
                                  {/* Name + Rank Badges Row */}
                                  <div className="flex flex-wrap items-center gap-2">
                                    <Username user={rev.user || rev} showBadge={false} hover={true} className="text-base font-chakra font-bold" />

                                    {/* Role Badge */}
                                    <span 
                                      className="px-2 py-0.5 text-[9px] font-black uppercase tracking-wider inline-flex items-center gap-1 border"
                                      style={{
                                        backgroundColor: `${rev.user?.group_color || groupColor}20`,
                                        borderColor: `${rev.user?.group_color || groupColor}50`,
                                        color: rev.user?.group_color || groupColor
                                      }}
                                    >
                                      <Crown className="w-2.5 h-2.5" />
                                      {rev.user?.group_name || userGroup}
                                    </span>

                                    {/* VIP Ribbon / Badge */}
                                    {(rev.user?.loadout?.banner_badge || rev.user?.vip_tier || rev.is_vip) && (
                                      <VipBadge tier={rev.user?.loadout?.banner_badge || rev.user?.vip_tier || "green"} />
                                    )}

                                    {/* Game Rank Badge */}
                                    {(rev.user?.cs2_rank || rev.cs2_rank) && (
                                      <span className="px-2 py-0.5 text-[9px] font-mono bg-white/5 border border-white/10 text-cyan-400 inline-flex items-center gap-1">
                                        <Gamepad2 className="w-2.5 h-2.5" />
                                        {rev.user?.cs2_rank || rev.cs2_rank}
                                      </span>
                                    )}
                                  </div>

                                  {/* Date & Verified Stamp Row */}
                                  <div className="flex items-center gap-2 text-[11px] font-mono text-zinc-400">
                                    <span className="flex items-center gap-1 text-zinc-300">
                                      <Clock className="w-3 h-3 text-[var(--tf-primary)]" />
                                      {formatReviewDate(rev.created_at)}
                                    </span>
                                    <span>•</span>
                                    <span className="text-emerald-400 font-bold flex items-center gap-1">
                                      <CheckCircle2 className="w-3 h-3" />
                                      Descărcare Verificată
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Right Rating Stars */}
                              <div className="flex sm:flex-col sm:items-end justify-between items-center gap-1">
                                <div className="flex items-center gap-1 text-amber-400">
                                  {Array.from({ length: 5 }).map((_, i) => (
                                    <Star 
                                      key={i} 
                                      className={`w-4 h-4 ${i < (rev.rating || 5) ? "fill-amber-400 text-amber-400" : "text-zinc-700"}`} 
                                    />
                                  ))}
                                </div>
                                <span className="text-xs font-black text-amber-400 font-mono">
                                  {(rev.rating || 5).toFixed(1)} / 5.0
                                </span>
                              </div>
                            </div>

                            {/* CENTER: Review Comment (Full Width) */}
                            <div className="py-2">
                              <p className="text-sm text-zinc-200 leading-relaxed whitespace-pre-line font-sans">
                                {rev.comment}
                              </p>
                            </div>

                            {/* BOTTOM: Action Bar */}
                            <div className="pt-3 border-t border-white/5 flex items-center justify-between text-[11px] font-mono text-zinc-500">
                              <span className="flex items-center gap-1 text-zinc-400">
                                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                                INDUNGI Security Shield • 100% Curat
                              </span>

                              <div className="flex items-center gap-2">
                                <button 
                                  onClick={() => toast.success("Ai apreciat această recenzie!")}
                                  className="flex items-center gap-1.5 px-3 py-1 bg-white/5 hover:bg-white/10 hover:text-white border border-white/10 text-zinc-300 transition-colors"
                                >
                                  <ThumbsUp className="w-3 h-3 text-[var(--tf-primary)]" />
                                  <span>Util ({(rev.rating || 5) > 3 ? 1 : 0})</span>
                                </button>

                                {(isStaff || (user?.id && rev.user_id === user.id) || (user?.username && rev.username === user.username)) && (
                                  <button
                                    onClick={() => handleDeleteReview(idx)}
                                    className="p-1 px-2 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 border border-transparent hover:border-red-500/20 transition-colors inline-flex items-center gap-1 text-[10px]"
                                    title="Șterge recenzia (Staff/Autor)"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                    <span>Șterge</span>
                                  </button>
                                )}
                              </div>
                            </div>

                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}

            </div>

            {/* RIGHT COLUMN (32%): Sticky Tactical Action Rail (Sharp borders) */}
            <div className="lg:col-span-4 space-y-6 sticky top-20">
              
              {/* PRIMARY DOWNLOAD CARD */}
              <div 
                className="bg-gradient-to-b from-[#131722] to-[#0a0d14] border-2 p-6 space-y-5 shadow-2xl relative overflow-hidden"
                style={{ borderColor: "var(--tf-primary)", boxShadow: "0 0 25px var(--tf-glow)" }}
              >
                <div className="space-y-1">
                  <span 
                    className="text-[10px] font-mono font-bold uppercase tracking-wider flex items-center gap-1"
                    style={{ color: "var(--tf-primary)" }}
                  >
                    <Zap className="w-3.5 h-3.5" /> High-Speed Direct Link
                  </span>
                  <h3 className="text-lg font-black text-white">Descarcă Fișierul Oficial</h3>
                  <p className="text-xs text-zinc-400 font-mono truncate">{res.file_name} ({res.file_size})</p>
                </div>

                {/* Primary Action Button */}
                <button
                  onClick={() => handleDownload(res)}
                  className="tf-btn-primary w-full py-4 text-sm font-black"
                >
                  <span>
                    <ArrowDownToLine className="w-5 h-5 mr-2" />
                    DOWNLOAD DIRECT ({res.file_size})
                  </span>
                </button>

                <div className="text-[11px] text-center text-zinc-400 flex items-center justify-center gap-2 pt-1 font-mono">
                  <span className="w-2 h-2 rounded-none bg-emerald-400 animate-ping" />
                  <span>CDN București • Viteză Maximă</span>
                </div>
              </div>

              {/* INDUNGI SECURITY SHIELD & INTEGRITY REPORT */}
              <div className="bg-[#0b0e15] border border-emerald-500/40 p-6 space-y-4 shadow-xl">
                <div className="flex items-center justify-between border-b border-white/10 pb-3">
                  <div className="flex items-center gap-2 text-emerald-400 font-black text-xs uppercase tracking-wide">
                    <ShieldCheck className="w-4 h-4" />
                    <span>INDUNGI Security Shield</span>
                  </div>
                  <span className="px-2 py-0.5 text-[9px] font-mono font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                    VERIFICAT 100%
                  </span>
                </div>
                
                <p className="text-xs text-zinc-300 leading-relaxed">
                  Pachet certificat oficial împotriva oricăror injectări slowhack, bind-uri de redirecționare forțată sau viruși.
                </p>

                <div className="space-y-2.5 font-mono text-[11px] pt-1">
                  <div className="flex items-center justify-between text-zinc-400">
                    <span>Scaner Anti-Slowhack:</span>
                    <span className="text-emerald-400 font-bold">0 Detecții / Curat</span>
                  </div>

                  <div className="flex items-center justify-between text-zinc-400">
                    <span>VirusTotal Report:</span>
                    <a 
                      href={res.virustotal_url || `https://www.virustotal.com/gui/file/${sha256Val}`} 
                      target="_blank" 
                      rel="noreferrer" 
                      className="text-emerald-400 font-bold hover:underline flex items-center gap-1"
                    >
                      0 / 72 Clean <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>

                  <div className="pt-2 border-t border-white/10 space-y-1.5">
                    <div className="flex items-center justify-between text-zinc-400">
                      <span className="text-[10px] text-zinc-500">SHA-256:</span>
                      <button 
                        onClick={() => copyChecksum(sha256Val, "sha")} 
                        className="text-xs text-zinc-300 hover:text-white flex items-center gap-1 bg-white/5 px-2 py-0.5"
                        title="Copiază SHA-256"
                      >
                        <span className="truncate max-w-[140px] text-[10px]">{sha256Val.slice(0, 16)}...</span>
                        {copiedSha ? <CheckCheck className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                      </button>
                    </div>

                    <div className="flex items-center justify-between text-zinc-400">
                      <span className="text-[10px] text-zinc-500">MD5:</span>
                      <button 
                        onClick={() => copyChecksum(md5Val, "md5")} 
                        className="text-xs text-zinc-300 hover:text-white flex items-center gap-1 bg-white/5 px-2 py-0.5"
                        title="Copiază MD5"
                      >
                        <span className="truncate max-w-[140px] text-[10px]">{md5Val.slice(0, 16)}...</span>
                        {copiedMd5 ? <CheckCheck className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* MIRRORS CARD */}
              {res.mirrors && res.mirrors.length > 1 && (
                <div className="bg-[#0b0e15] border border-white/15 p-6 space-y-3 shadow-xl">
                  <h4 className="text-xs font-black text-zinc-400 uppercase tracking-wider font-mono flex items-center gap-2">
                    <Server className="w-4 h-4 text-cyan-400" /> Link-uri Mirror Alternative
                  </h4>
                  <div className="space-y-2">
                    {res.mirrors.map((m, idx) => (
                      <a
                        key={idx}
                        href={m.url}
                        target="_blank"
                        rel="noreferrer"
                        className="tf-btn-dark w-full py-2.5 text-xs justify-between"
                      >
                        <span className="truncate">{m.name}</span>
                        <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

            </div>

          </div>
        </div>

        {/* Modals for Detail View */}
        {renderEditModal()}
        {renderSecurityReportModal()}

      </div>
    );
  }

  // -------------------------------------------------------------
  // RENDER 2: MAIN CATALOG BROWSE VIEW (SHARP CORNERS & DYNAMIC THEME)
  // -------------------------------------------------------------
  return (
    <div className="min-h-screen bg-[#07090e] text-zinc-100 selection:bg-[var(--tf-primary)] selection:text-black">
      
      {/* 1. TOP HERO SECTION */}
      <section className="relative border-b border-white/10 bg-gradient-to-b from-[#0e121c] via-[#090c12] to-[#07090e] pt-10 pb-12 px-4 md:px-8 overflow-hidden">
        <div 
          className="absolute top-0 right-1/4 w-[600px] h-[600px] rounded-none blur-[180px] pointer-events-none opacity-20"
          style={{ backgroundColor: "var(--tf-glow)" }}
        />

        <div className="max-w-7xl mx-auto space-y-6 relative z-10">
          
          {/* Top Title & Community Metrics Banner */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 pb-2 border-b border-white/10">
            <div className="space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] font-mono font-bold text-zinc-400 flex items-center gap-1.5">
                  <span className="text-[var(--tf-primary)]">INDUNGI</span>
                  <span className="text-zinc-600">/</span>
                  <span className="text-zinc-200">Catalog Resurse</span>
                </span>
                <span className="px-2 py-0.5 text-[9px] font-mono font-black uppercase text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3" /> VERIFICAT ANTI-SLOWHACK
                </span>
              </div>

              <h1 className="text-2xl sm:text-4xl md:text-5xl font-black tracking-tight text-white uppercase font-chakra">
                Download & Resurse Gaming
              </h1>

              <p className="text-xs sm:text-sm text-zinc-400 max-w-2xl leading-relaxed">
                Descărcări oficiale de mare viteză: Kituri CS 1.6 & CS2, CFG-uri pro, Mappack-uri, Addons & Pluginuri 100% verificate.
              </p>
            </div>

            {/* Quick Actions & Upload Button */}
            <div className="flex items-center gap-3 flex-wrap lg:flex-nowrap flex-shrink-0">
              <button
                onClick={() => {
                  if (!user) {
                    toast.error("Trebuie să fii autentificat pentru a încărca fișiere.");
                    return;
                  }
                  setShowUploadModal(true);
                }}
                className="tf-btn-primary px-6 py-3 text-xs font-black flex items-center gap-2 shadow-xl"
              >
                <span>
                  <Upload className="w-4 h-4 mr-1.5" />
                  Încarcă Resursă
                </span>
              </button>
            </div>
          </div>

          {/* MODERN UNIFIED SEARCH & GAME SELECTOR BAR */}
          <div className="bg-[#0b0e15] border border-white/15 p-2 sm:p-3 shadow-2xl space-y-2.5">
            <div className="flex flex-col md:flex-row items-center gap-2">
              {/* Search Form */}
              <form onSubmit={handleSearch} className="relative flex items-center flex-1 w-full">
                <Search className="w-4 h-4 text-zinc-400 absolute left-3.5" />
                <input
                  type="text"
                  placeholder="Caută kituri CS, de_dust2, CFG s1mple, addons ReHLDS..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full bg-black/60 border border-white/10 pl-10 pr-24 py-2.5 text-xs text-white placeholder-zinc-500 outline-none transition-all focus:border-[var(--tf-primary)]"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => { setSearchQuery(""); fetchData(); }}
                    className="absolute right-12 text-zinc-500 hover:text-white p-1"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
                <button
                  type="submit"
                  className="absolute right-1.5 px-3 py-1.5 bg-white/10 hover:bg-[var(--tf-primary)] hover:text-black text-white text-[11px] font-bold transition-colors"
                >
                  Caută
                </button>
              </form>

              {/* Game Switcher Tabs */}
              <div className="flex items-center bg-black/60 border border-white/10 p-0.5 w-full md:w-auto">
                {[
                  { id: "all", label: "Toate" },
                  { id: "cs16", label: "CS 1.6" },
                  { id: "cs2", label: "CS2" },
                ].map(g => (
                  <button
                    key={g.id}
                    onClick={() => setSearchParams({ category: activeCategory, game: g.id, sort: sortBy })}
                    className={`flex-1 md:flex-initial px-4 py-2 text-xs font-black tracking-wide transition-all ${
                      gameFilter === g.id
                        ? "text-black font-black"
                        : "text-zinc-400 hover:text-white hover:bg-white/5"
                    }`}
                    style={{
                      backgroundColor: gameFilter === g.id ? "var(--tf-primary)" : undefined
                    }}
                  >
                    {g.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Quick Trending Tags */}
            <div className="flex flex-wrap items-center gap-1.5 pt-1 text-xs border-t border-white/5">
              <span className="font-mono text-[10px] text-zinc-500 uppercase tracking-wider font-bold flex items-center gap-1 pr-1">
                <Tag className="w-3 h-3 text-[var(--tf-primary)]" /> Căutări Rapide:
              </span>
              {[
                { label: "CS 1.6 Clean Edition", cat: "clienti" },
                { label: "s1mple & donk CFG", cat: "cfg" },
                { label: "ReHLDS AMXX 1.10 Addons", cat: "pluginuri" },
                { label: "Ultimate MapPack 50+", cat: "harti" },
                { label: "Quake Sounds 320kbps", cat: "sunete" },
              ].map((t, idx) => (
                <button
                  key={idx}
                  onClick={() => setSearchParams({ category: t.cat, game: "all", sort: sortBy })}
                  className="px-2.5 py-0.5 bg-white/5 hover:bg-[var(--tf-primary)] hover:text-black border border-white/10 text-[10px] font-mono font-medium text-zinc-300 transition-all shadow-sm"
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* 2. MAIN LAYOUT: SIDEBAR + CONTENT AREA (Sharp Rectangular Design) */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* LEFT SIDEBAR: CATEGORY NAVIGATION & ACTIONS */}
          <aside className="lg:col-span-3 space-y-4 sticky top-20">
            {/* Primary Add Resource CTA (XenForo / NullForums Standard) */}
            <button
              onClick={() => setShowUploadModal(true)}
              className="tf-btn-primary w-full py-3 text-xs font-black flex items-center justify-center gap-2 shadow-xl"
            >
              <Upload className="w-4 h-4" />
              <span>Adaugă Resursă Nouă</span>
            </button>

            <div className="bg-[#0b0e15] border border-white/15 p-4 shadow-xl space-y-2">
              <div className="flex items-center justify-between pb-3 border-b border-white/10 px-2">
                <span 
                  className="text-xs font-black uppercase tracking-wider flex items-center gap-2"
                  style={{ color: "var(--tf-primary)" }}
                >
                  <Filter className="w-3.5 h-3.5" />
                  Categorii
                </span>
                <span className="text-[10px] font-mono text-zinc-500 font-bold">{resources.length} Fișiere</span>
              </div>

              <div className="space-y-1">
                {CATEGORY_MAP.map(cat => {
                  const Icon = cat.icon;
                  const isActive = activeCategory === cat.id;
                  const catStat = categories.find(c => c.id === cat.id);
                  const count = catStat ? catStat.count : (cat.id === "all" ? resources.length : 0);

                  return (
                    <button
                      key={cat.id}
                      onClick={() => setSearchParams({ category: cat.id, game: gameFilter, sort: sortBy })}
                      className={`w-full p-3 text-left transition-all flex items-center justify-between group ${
                        isActive
                          ? "text-black font-black"
                          : "text-zinc-400 hover:bg-white/5 hover:text-white"
                      }`}
                      style={{
                        backgroundColor: isActive ? "var(--tf-primary)" : undefined
                      }}
                    >
                      <div className="flex items-center gap-2.5 overflow-hidden">
                        <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? "text-black" : "text-zinc-400 group-hover:text-white"}`} />
                        <span className="text-xs truncate font-bold">{cat.name}</span>
                      </div>
                      <span className={`text-[10px] font-mono font-bold px-2 py-0.5 ${
                        isActive ? "bg-black/20 text-black font-black" : "bg-white/5 text-zinc-500 group-hover:text-zinc-300"
                      }`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* WIDGET 1: TOP UPLOADERS / AUTORI */}
            {leaderboard.top_uploaders && leaderboard.top_uploaders.length > 0 && (
              <div className="bg-[#0b0e15] border border-white/15 p-4 shadow-xl space-y-3">
                <div className="flex items-center justify-between pb-2.5 border-b border-white/10 px-1">
                  <span 
                    className="text-xs font-black uppercase tracking-wider flex items-center gap-1.5"
                    style={{ color: "var(--tf-primary)" }}
                  >
                    <Crown className="w-3.5 h-3.5" />
                    Top Autori Resurse
                  </span>
                  <span className="text-[10px] font-mono text-zinc-500 font-bold">Fișiere</span>
                </div>

                <div className="space-y-2">
                  {leaderboard.top_uploaders.map((up, idx) => {
                    const medalColor = idx === 0 ? "text-amber-400 font-black" : idx === 1 ? "text-zinc-300 font-bold" : idx === 2 ? "text-amber-600 font-bold" : "text-zinc-600 font-mono";
                    const medalSymbol = idx === 0 ? "🥇" : idx === 1 ? "🥈" : idx === 2 ? "🥉" : `#${idx + 1}`;

                    return (
                      <div 
                        key={idx} 
                        className="flex items-center justify-between p-2 bg-black/40 border border-white/5 hover:border-[var(--tf-primary)]/40 transition-colors"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`text-xs w-4 text-center ${medalColor}`}>{medalSymbol}</span>
                          <Avatar user={up.user} size={22} hover={true} />
                          <div className="min-w-0">
                            <Username 
                              user={up.user} 
                              showBadge={false} 
                              hover={true} 
                              className="text-xs font-chakra font-bold truncate block text-zinc-200" 
                            />
                            <span className="text-[10px] font-mono text-zinc-500 block">
                              {up.total_downloads?.toLocaleString()} descărcări
                            </span>
                          </div>
                        </div>

                        <span className="px-2 py-0.5 text-[10px] font-mono font-black bg-white/5 text-[var(--tf-primary)] border border-white/10">
                          {up.uploads_count} {up.uploads_count === 1 ? "fișier" : "fișiere"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* WIDGET 2: TOP DESCARCARI */}
            {leaderboard.top_downloads && leaderboard.top_downloads.length > 0 && (
              <div className="bg-[#0b0e15] border border-white/15 p-4 shadow-xl space-y-3">
                <div className="flex items-center justify-between pb-2.5 border-b border-white/10 px-1">
                  <span className="text-xs font-black uppercase tracking-wider flex items-center gap-1.5 text-amber-400">
                    <Zap className="w-3.5 h-3.5 fill-amber-400" />
                    Top Descărcări
                  </span>
                  <span className="text-[10px] font-mono text-zinc-500 font-bold">Popularitate</span>
                </div>

                <div className="space-y-2">
                  {leaderboard.top_downloads.map((res, idx) => (
                    <div
                      key={res.id || idx}
                      onClick={() => openDetail(res)}
                      className="flex items-center justify-between p-2 bg-black/40 border border-white/5 hover:border-[var(--tf-primary)] transition-all cursor-pointer group"
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span className="text-xs font-mono font-black text-zinc-500 group-hover:text-[var(--tf-primary)] w-4 text-center">
                          #{idx + 1}
                        </span>
                        <div className="min-w-0">
                          <h4 className="text-xs font-bold text-white group-hover:text-[var(--tf-primary)] transition-colors truncate font-chakra">
                            {res.title}
                          </h4>
                          <span className="text-[10px] font-mono text-zinc-500">
                            {(res.game || "cs16").toUpperCase()} • {res.file_size}
                          </span>
                        </div>
                      </div>

                      <span className="text-[10px] font-mono font-bold text-emerald-400 flex items-center gap-1 flex-shrink-0">
                        <Download className="w-3 h-3" /> {res.downloads_count?.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* WIDGET 3: STATISTICI CATALOG */}
            {leaderboard.stats && (
              <div className="bg-[#0b0e15] border border-white/15 p-4 shadow-xl space-y-2.5">
                <div className="flex items-center gap-1.5 text-xs font-black uppercase tracking-wider text-white px-1 pb-2 border-b border-white/10">
                  <SlidersHorizontal className="w-3.5 h-3.5 text-[var(--tf-primary)]" />
                  <span>Statistici Comunitate</span>
                </div>

                <div className="grid grid-cols-2 gap-2 text-center pt-1 font-mono">
                  <div className="p-2.5 bg-black/40 border border-white/5">
                    <span className="text-sm font-black text-white block">{leaderboard.stats.total_resources}</span>
                    <span className="text-[9px] text-zinc-500 uppercase tracking-wider">Total Fișiere</span>
                  </div>
                  <div className="p-2.5 bg-black/40 border border-white/5">
                    <span className="text-sm font-black text-emerald-400 block">{leaderboard.stats.total_downloads?.toLocaleString()}</span>
                    <span className="text-[9px] text-zinc-500 uppercase tracking-wider">Descărcări</span>
                  </div>
                  <div className="p-2.5 bg-black/40 border border-white/5">
                    <span className="text-sm font-black text-[var(--tf-primary)] block">{leaderboard.stats.total_uploaders}</span>
                    <span className="text-[9px] text-zinc-500 uppercase tracking-wider">Autori Activi</span>
                  </div>
                  <div className="p-2.5 bg-black/40 border border-white/5">
                    <span className="text-sm font-black text-cyan-400 block">100%</span>
                    <span className="text-[9px] text-zinc-500 uppercase tracking-wider">Scanat & Curat</span>
                  </div>
                </div>
              </div>
            )}

            {/* High-Speed Download Guarantee Box */}
            <div className="bg-[#0b0e15] border border-emerald-500/30 p-5 space-y-3 shadow-lg">
              <div className="flex items-center gap-2 text-emerald-400 font-black text-xs uppercase tracking-wide">
                <ShieldCheck className="w-4 h-4" />
                <span>Garanție Securitate INDUNGI</span>
              </div>
              <p className="text-[11px] text-zinc-400 leading-relaxed">
                Toate kiturile și fișierele sunt stocate pe servere dedicate de mare viteză și sunt scanate automat cu VirusTotal împotriva oricărui slowhack sau malware.
              </p>
            </div>
          </aside>

          {/* MAIN CONTENT AREA */}
          <main className="lg:col-span-9 space-y-6">
            
            {/* HERO FEATURED HIGHLIGHT (Sharp angular card) */}
            {activeCategory === "all" && primaryFeatured && (
              <div 
                className="relative overflow-hidden border-2 bg-gradient-to-r from-[#121622] via-[#0b0e15] to-black p-6 md:p-8 shadow-2xl group"
                style={{ borderColor: "var(--tf-primary)", boxShadow: "0 0 20px var(--tf-glow)" }}
              >
                <div 
                  className="absolute top-0 right-0 w-96 h-96 blur-[120px] pointer-events-none opacity-20"
                  style={{ backgroundColor: "var(--tf-glow)" }}
                />
                
                <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <div className="space-y-3 max-w-xl">
                    <div className="flex items-center gap-2">
                      <span 
                        className="px-2.5 py-1 text-[10px] font-black uppercase text-black flex items-center gap-1"
                        style={{ backgroundColor: "var(--tf-primary)" }}
                      >
                        <Award className="w-3.5 h-3.5" /> RECOMANDAT INDUNGI
                      </span>
                      <span className="px-2.5 py-1 text-[10px] font-mono font-bold bg-white/10 text-zinc-300">
                        {(primaryFeatured?.game || "cs16").toUpperCase()} • {primaryFeatured?.version || "v1.0"}
                      </span>
                    </div>

                    <h2 
                      className="text-xl md:text-2xl font-black text-white leading-tight transition-colors font-chakra"
                    >
                      {primaryFeatured?.title}
                    </h2>

                    <p className="text-xs text-zinc-300 line-clamp-2 leading-relaxed">
                      {primaryFeatured?.description}
                    </p>

                    <div className="flex flex-wrap items-center gap-3 text-xs font-mono text-zinc-400 pt-1">
                      <div className="flex items-center gap-2">
                        <Avatar 
                          user={primaryFeatured?.uploader || {
                            username: primaryFeatured?.uploaded_by || primaryFeatured?.author || 'User',
                            avatar_url: primaryFeatured?.uploaded_by_avatar || '',
                            role: 'user',
                            group_name: 'MEMBRU',
                            group_color: '#71717A',
                            loadout: {}
                          }} 
                          size={28} 
                          hover={true} 
                        />
                        <span className="text-zinc-400">Postat de:</span>
                        <Username 
                          user={primaryFeatured?.uploader || {
                            username: primaryFeatured?.uploaded_by || primaryFeatured?.author || 'User',
                            avatar_url: primaryFeatured?.uploaded_by_avatar || '',
                            role: 'user',
                            group_name: 'MEMBRU',
                            group_color: '#71717A',
                            loadout: {}
                          }} 
                          showBadge={false} 
                          hover={true} 
                          className="font-chakra font-bold text-white text-xs" 
                        />
                      </div>
                      <span className="text-zinc-600">•</span>
                      <span className="text-emerald-400 font-bold flex items-center gap-1">
                        <Download className="w-3.5 h-3.5" /> {primaryFeatured?.downloads_count?.toLocaleString() || 0} descărcări
                      </span>
                      <span className="text-zinc-600">•</span>
                      <span className="flex items-center gap-1 text-amber-400 font-bold">
                        <Star className="w-3.5 h-3.5 fill-amber-400" /> {primaryFeatured?.rating_avg || 5.0} ({primaryFeatured?.ratings_count || 0} recenzii)
                      </span>
                      <span className="text-zinc-600">•</span>
                      <span className="text-zinc-300 font-bold">{primaryFeatured?.file_size}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col sm:flex-row md:flex-col gap-3 flex-shrink-0">
                    <button
                      onClick={() => handleDownload(primaryFeatured)}
                      className="tf-btn-primary px-6 py-3.5 text-xs font-black"
                    >
                      <span>
                        <ArrowDownToLine className="w-4 h-4 mr-1.5" />
                        Descarcă Acum ({primaryFeatured?.file_size})
                      </span>
                    </button>

                    <button
                      onClick={() => openDetail(primaryFeatured)}
                      className="tf-btn-dark px-5 py-2.5 text-xs text-center"
                    >
                      <span>
                        Vezi Pagina Completă
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* RESOURCE LIST HEADER WITH FILTER TABS (XenForo / NullForums Architecture) */}
            <div className="bg-[#0b0e15] border border-white/10 p-2 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-md">
              <div className="flex items-center gap-1 overflow-x-auto py-1">
                {[
                  { id: "latest", label: "⚡ Ultimele Adăugate" },
                  { id: "popular", label: "🔥 Top Descărcări" },
                  { id: "rating", label: "⭐ Cel mai Bine Cotate" },
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setSearchParams({ category: activeCategory, game: gameFilter, sort: tab.id })}
                    className={`px-3 py-1.5 text-xs font-black tracking-wide whitespace-nowrap transition-all ${
                      sortBy === tab.id
                        ? "text-black font-black"
                        : "text-zinc-400 hover:text-white hover:bg-white/5"
                    }`}
                    style={{
                      backgroundColor: sortBy === tab.id ? "var(--tf-primary)" : undefined
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <span className="text-xs text-zinc-500 font-mono px-2 self-center font-bold">
                {resources.length} resurse disponibile
              </span>
            </div>

            {/* RESOURCE ROWS (XenForo / NullForums XFRM Professional List) */}
            {loading ? (
              <div className="p-16 text-center text-zinc-500 font-mono text-xs bg-[#0b0e15] border border-white/10">
                Se încarcă catalogul de resurse...
              </div>
            ) : resources.length === 0 ? (
              <div className="p-16 text-center bg-[#0b0e15] border border-white/10 space-y-3">
                <AlertCircle className="w-10 h-10 text-zinc-600 mx-auto" />
                <h4 className="text-sm font-bold text-white">Nicio resursă găsită</h4>
                <p className="text-xs text-zinc-400">Încearcă să schimbi termenul de căutare sau categoria selectată.</p>
                <button
                  onClick={() => setSearchParams({ category: "all", game: "all", sort: "latest" })}
                  className="tf-btn-secondary px-4 py-2 text-xs"
                >
                  <span>Resetează Filtrele</span>
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {resources.map(res => {
                  const authorUser = res.uploader || {
                    username: res.uploaded_by || res.author || 'User',
                    avatar_url: res.uploaded_by_avatar || '',
                    role: 'user',
                    group_name: 'MEMBRU',
                    group_color: '#71717A',
                    loadout: {}
                  };

                  const thumbImg = res.screenshots?.[0] || res.screenshot || 
                    (res.game === "cs2" 
                      ? "https://images.unsplash.com/photo-1542751371-adc38448a05e?w=500&auto=format&fit=crop&q=60"
                      : "https://images.unsplash.com/photo-1511512578047-dfb367046420?w=500&auto=format&fit=crop&q=60");

                  const gameLabel = res?.game === "cs2" ? "CS2" : (res?.game === "cs16" || !res?.game || res?.game === "all") ? "CS 1.6" : res.game.toUpperCase();
                  const catObj = CATEGORY_MAP.find(c => c.id === res.category);

                  return (
                    <div
                      key={res.id}
                      onClick={() => openDetail(res)}
                      className="bg-[#0b0e15] border border-white/10 hover:border-[var(--tf-primary)] p-3.5 sm:p-4 transition-all duration-200 group flex flex-col md:flex-row items-start md:items-center justify-between gap-4 cursor-pointer shadow-lg hover:shadow-2xl relative overflow-hidden"
                    >
                      {/* Left: 72x72 Resource Icon + Info Column */}
                      <div className="flex items-start gap-3.5 min-w-0 flex-1 w-full md:w-auto">
                        
                        {/* 1. Square Icon / Preview */}
                        <div className="w-16 h-16 sm:w-18 sm:h-18 flex-shrink-0 bg-black relative border border-white/10 overflow-hidden flex items-center justify-center group-hover:border-[var(--tf-primary)] transition-colors">
                          <img 
                            src={thumbImg} 
                            alt={res.title}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110 opacity-90 group-hover:opacity-100"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "https://images.unsplash.com/photo-1542751371-adc38448a05e?w=500&auto=format&fit=crop&q=60";
                            }}
                          />

                          {/* Game badge on icon */}
                          <span 
                            className="absolute top-1 left-1 px-1.5 py-0.2 text-[8px] font-mono font-black uppercase text-black"
                            style={{ backgroundColor: res?.game === "cs2" ? "#00F0FF" : "var(--tf-primary)" }}
                          >
                            {gameLabel}
                          </span>

                          {/* NEW badge */}
                          {isNewResource(res.created_at || res.updated_at) && (
                            <span className="absolute top-1 right-1 px-1 py-0.2 text-[7px] font-black uppercase bg-[#00F0FF] text-black shadow-sm">
                              NEW
                            </span>
                          )}
                        </div>

                        {/* 2. Middle Content Column */}
                        <div className="space-y-1 min-w-0 flex-1">
                          {/* Row 1: Category Tag + Title + Version + Verified Status */}
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="px-1.5 py-0.5 text-[9px] font-mono font-bold uppercase bg-white/5 text-zinc-400 border border-white/10">
                              {catObj?.name || "Resursă"}
                            </span>

                            <h3 className="text-sm sm:text-base font-black text-white group-hover:text-[var(--tf-primary)] transition-colors line-clamp-1 font-chakra">
                              {res.title}
                            </h3>

                            <span className="text-[11px] font-mono text-zinc-400 font-bold bg-black/60 px-1.5 py-0.5 border border-white/10">
                              {res.version || "1.0.0"}
                            </span>

                            {res.is_verified && (
                              <span className="text-[10px] font-bold text-emerald-400 flex items-center gap-0.5 whitespace-nowrap">
                                <ShieldCheck className="w-3 h-3 text-emerald-400" /> Curat
                              </span>
                            )}
                          </div>

                          {/* Row 2: Tagline / Description */}
                          <p className="text-xs text-zinc-400 line-clamp-1 leading-relaxed">
                            {res.description}
                          </p>

                          {/* Row 3: Author Meta Row */}
                          <div className="flex items-center gap-2.5 text-xs text-zinc-400 pt-0.5 flex-wrap" onClick={e => e.stopPropagation()}>
                            <div className="flex items-center gap-1.5">
                              <Avatar user={authorUser} size={18} hover={true} />
                              <Username 
                                user={authorUser} 
                                showBadge={false} 
                                hover={true} 
                                className="text-xs font-chakra font-bold text-zinc-300 hover:text-white" 
                              />
                            </div>

                            <span className="text-zinc-600">•</span>

                            <span className="text-[11px] font-mono text-zinc-400 flex items-center gap-1">
                              <Clock className="w-3 h-3 text-[var(--tf-primary)]" />
                              {formatReviewDate(res.created_at || res.updated_at)}
                            </span>

                            <span className="text-zinc-600">•</span>

                            <span className="text-[11px] font-mono text-zinc-400">
                              {res.file_size}
                            </span>
                          </div>
                        </div>

                      </div>

                      {/* Right: Rating Stars + Downloads Counter + Direct Download Button */}
                      <div className="flex items-center md:flex-col md:items-end justify-between w-full md:w-auto gap-3 flex-shrink-0 pt-2 md:pt-0 border-t md:border-t-0 border-white/5" onClick={e => e.stopPropagation()}>
                        {/* Rating & Downloads */}
                        <div className="flex items-center md:flex-col md:items-end gap-2.5 md:gap-0.5">
                          <div className="flex items-center gap-1 text-xs text-amber-400 font-bold font-mono">
                            <div className="flex items-center">
                              {[...Array(5)].map((_, i) => (
                                <Star key={i} className={`w-3 h-3 ${i < Math.round(res.rating_avg || 5) ? "fill-amber-400 text-amber-400" : "text-zinc-600"}`} />
                              ))}
                            </div>
                            <span className="text-zinc-500 text-[10px]">({res.ratings_count || 0})</span>
                          </div>

                          <span className="text-[11px] font-mono text-zinc-400 flex items-center gap-1">
                            <Download className="w-3 h-3 text-emerald-400" /> {res.downloads_count?.toLocaleString()} descărcări
                          </span>
                        </div>

                        {/* Direct Download Button */}
                        <button
                          onClick={() => handleDownload(res)}
                          className="tf-btn-primary px-4 py-1.5 text-xs font-black"
                        >
                          <span>
                            <ArrowDownToLine className="w-3.5 h-3.5 mr-1" />
                            DESCARCĂ
                          </span>
                        </button>
                      </div>

                    </div>
                  );
                })}
              </div>
            )}

          </main>

        </div>
      </div>

      {/* 3. UPLOAD MODAL WITH REAL-TIME INDUNGI SECURITY SCANNER */}
      {showUploadModal && createPortal(
        <div className="fixed inset-0 z-[999999] bg-black/90 backdrop-blur-md flex items-center justify-center p-4 py-8 md:py-12 overflow-y-auto">
          <div className="relative w-full max-w-2xl bg-[#0b0e15] border border-white/20 p-6 md:p-8 shadow-2xl space-y-4 my-auto max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <h2 className="text-lg font-black text-white flex items-center gap-2 font-chakra">
                <ShieldCheck className="w-5 h-5 text-emerald-400" />
                Încarcă Resursă & Scanare Securitate INDUNGI
              </h2>
              <button onClick={() => setShowUploadModal(false)} className="p-1.5 text-zinc-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* SOURCE SELECTOR TABS */}
            <div className="flex items-center gap-2 border-b border-white/10 pb-3">
              <button
                type="button"
                onClick={() => {
                  setUploadMode("file");
                }}
                className={`tf-btn-skew px-4 py-2 text-xs font-black transition-all ${
                  uploadMode === "file"
                    ? "tf-btn-primary"
                    : "bg-white/5 text-zinc-400 hover:text-white border border-white/10"
                }`}
              >
                <span>
                  <Upload className="w-3.5 h-3.5 mr-1.5" />
                  📁 Încarcă Fișier din PC (Automat)
                </span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setUploadMode("url");
                }}
                className={`tf-btn-skew px-4 py-2 text-xs font-black transition-all ${
                  uploadMode === "url"
                    ? "tf-btn-primary"
                    : "bg-white/5 text-zinc-400 hover:text-white border border-white/10"
                }`}
              >
                <span>
                  <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                  🌐 Link Extern (Cloud / Mirror)
                </span>
              </button>
            </div>

            <form onSubmit={handleCreateResource} className="space-y-3.5 text-xs">
              <div>
                <label className="block text-zinc-400 font-bold mb-1">Titlu Resursă *</label>
                <input
                  type="text"
                  required
                  placeholder="ex: Counter-Strike 1.6 Clean Edition 2026..."
                  value={upTitle}
                  onChange={e => setUpTitle(e.target.value)}
                  className="w-full bg-black/60 border border-white/15 p-3 text-white outline-none font-bold"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-zinc-400 font-bold mb-1">Categorie *</label>
                  <select
                    value={upCategory}
                    onChange={e => setUpCategory(e.target.value)}
                    className="w-full bg-black/60 border border-white/15 p-3 text-white outline-none"
                  >
                    <option value="clienti">Clienți & Kituri CS</option>
                    <option value="cfg">Configuri & CFG Pro</option>
                    <option value="harti">Hărți & Mappack</option>
                    <option value="modele">Modele & Skin-uri</option>
                    <option value="pluginuri">Addons & Servere</option>
                    <option value="gui">GUI & Teme</option>
                    <option value="sunete">Sunete & Voice</option>
                  </select>
                </div>
                <div>
                  <label className="block text-zinc-400 font-bold mb-1">Joc *</label>
                  <select
                    value={upGame}
                    onChange={e => setUpGame(e.target.value)}
                    className="w-full bg-black/60 border border-white/15 p-3 text-white outline-none"
                  >
                    <option value="cs16">Counter-Strike 1.6</option>
                    <option value="cs2">Counter-Strike 2</option>
                  </select>
                </div>
              </div>

              {/* MODE 1: DIRECT FILE UPLOAD (100% AUTOMATED) */}
              {uploadMode === "file" ? (
                <div className="space-y-3 p-4 bg-black/60 border border-white/15">
                  <div className="flex items-center justify-between">
                    <label className="block text-white font-bold flex items-center gap-2">
                      <Lock className="w-4 h-4 text-emerald-400" />
                      Selectează Fișierul din Calculator *
                    </label>
                    <span className="text-[10px] font-mono text-zinc-400 uppercase bg-white/5 px-2 py-0.5 border border-white/10">
                      Auto Hash & Shield Scan
                    </span>
                  </div>

                  <input
                    type="file"
                    required={!upFileUrl}
                    onChange={handleFileUploadToBackend}
                    disabled={uploadingFile}
                    className="w-full bg-black border border-white/20 p-2.5 text-zinc-300 file:mr-4 file:py-1.5 file:px-3 file:border-0 file:text-xs file:font-black file:bg-[var(--tf-primary)] file:text-black file:cursor-pointer"
                  />

                  {uploadingFile && (
                    <div className="space-y-1.5 pt-1">
                      <div className="flex justify-between text-[11px] font-mono">
                        <span className="text-cyan-400 flex items-center gap-1.5">
                          <ShieldCheck className="w-3.5 h-3.5 animate-spin text-cyan-400" />
                          Se încarcă și se scanează arhiva...
                        </span>
                        <span className="text-white font-bold">{uploadProgress}%</span>
                      </div>
                      <div className="w-full bg-white/10 h-2 overflow-hidden border border-white/10">
                        <div 
                          className="bg-gradient-to-r from-cyan-500 to-emerald-500 h-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {upFileUrl && !uploadingFile && (
                    <div className="p-2.5 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs flex items-center justify-between font-mono">
                      <span className="flex items-center gap-1.5">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                        Fișier scanat și pregătit ({upFileSize}): {upFileName}
                      </span>
                      <span className="text-[10px] bg-emerald-500/20 px-1.5 py-0.5 text-emerald-300">
                        0 Amenințări
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                /* MODE 2: EXTERNAL URL (MANUAL) */
                <div className="space-y-3 p-4 bg-black/60 border border-white/15">
                  <div>
                    <label className="block text-zinc-400 font-bold mb-1">URL Descărcare Fișier *</label>
                    <input
                      type="url"
                      required={uploadMode === "url"}
                      placeholder="https://mega.nz/... sau https://drive.google.com/..."
                      value={upFileUrl}
                      onChange={e => setUpFileUrl(e.target.value)}
                      className="w-full bg-black/60 border border-white/15 p-2.5 text-white outline-none font-mono"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-zinc-400 font-bold mb-1">Mărime Fișier (ex: 250 MB)</label>
                      <input
                        type="text"
                        placeholder="ex: 250 MB"
                        value={upFileSize}
                        onChange={e => setUpFileSize(e.target.value)}
                        className="w-full bg-black/60 border border-white/15 p-2.5 text-white outline-none font-mono"
                      />
                    </div>
                    <div>
                      <label className="block text-zinc-400 font-bold mb-1">Nume Fișier (ex: CS_Pack.zip)</label>
                      <input
                        type="text"
                        placeholder="resource.zip"
                        value={upFileName}
                        onChange={e => setUpFileName(e.target.value)}
                        className="w-full bg-black/60 border border-white/15 p-2.5 text-white outline-none font-mono"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-zinc-400 font-bold mb-1">Versiune</label>
                  <input
                    type="text"
                    value={upVersion}
                    onChange={e => setUpVersion(e.target.value)}
                    className="w-full bg-black/60 border border-white/15 p-2.5 text-white outline-none font-mono"
                  />
                </div>
                <div>
                  <label className="block text-zinc-400 font-bold mb-1">Autor</label>
                  <input
                    type="text"
                    placeholder={user?.username || "Autor"}
                    value={upAuthor}
                    onChange={e => setUpAuthor(e.target.value)}
                    className="w-full bg-black/60 border border-white/15 p-2.5 text-white outline-none"
                  />
                </div>
              </div>

              {/* BANNER / SCREENSHOT IMAGE FOR NEW RESOURCE */}
              <div className="p-3 bg-black/40 border border-white/15 space-y-2.5">
                <div className="flex items-center justify-between">
                  <label className="text-zinc-200 font-bold flex items-center gap-1.5 font-chakra text-xs">
                    <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
                    Imagine Banner / Previzualizare (16:9)
                  </label>
                  <div>
                    <input 
                      type="file" 
                      id="upload-banner-file-input"
                      accept="image/*" 
                      style={{ display: "none" }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = (ev) => {
                            setUpScreenshot(ev.target.result);
                          };
                          reader.readAsDataURL(file);
                        }
                      }} 
                    />
                    <button
                      type="button"
                      onClick={() => document.getElementById("upload-banner-file-input")?.click()}
                      className="tf-btn-secondary px-3 py-1 text-[11px] font-bold flex items-center gap-1.5 cursor-pointer shadow-md"
                    >
                      <Upload className="w-3 h-3" />
                      <span>Alege Poză din PC</span>
                    </button>
                  </div>
                </div>

                <input
                  type="text"
                  placeholder="URL imagine banner (sau selectează un preset rapid)"
                  value={upScreenshot}
                  onChange={e => setUpScreenshot(e.target.value)}
                  className="w-full bg-black/80 border border-white/15 p-2 text-white outline-none font-mono text-xs focus:border-cyan-400"
                />

                {/* Preseturi CS Oficiale */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[10px] font-mono text-zinc-500 font-bold uppercase">Preseturi CS:</span>
                  {[
                    { label: "CS 1.6 HD Clasic", url: "https://shared.fastly.steamstatic.com/store_item_assets/steam/apps/10/capsule_616x353.jpg" },
                    { label: "CS2 Premier HD", url: "https://shared.fastly.steamstatic.com/store_item_assets/steam/apps/730/capsule_616x353.jpg" },
                    { label: "CS: Source", url: "https://shared.fastly.steamstatic.com/store_item_assets/steam/apps/240/capsule_616x353.jpg" },
                    { label: "CS 1.6 Retro", url: "https://cdn.cloudflare.steamstatic.com/steam/apps/10/header.jpg" }
                  ].map((preset, pIdx) => (
                    <button
                      key={pIdx}
                      type="button"
                      onClick={() => setUpScreenshot(preset.url)}
                      className="px-2 py-0.5 bg-white/5 hover:bg-cyan-500/20 text-[10px] font-mono text-zinc-300 hover:text-cyan-300 border border-white/10 hover:border-cyan-400 transition-colors font-bold"
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>

                {upScreenshot && (
                  <div className="relative w-full aspect-[16/9] max-h-36 overflow-hidden border border-white/20 bg-black/90 shadow-md">
                    <img 
                      src={upScreenshot} 
                      alt="Banner Preview" 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://shared.fastly.steamstatic.com/store_item_assets/steam/apps/10/capsule_616x353.jpg";
                      }}
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-zinc-400 font-bold mb-1">Descriere Resursă *</label>
                <textarea
                  rows={3}
                  required
                  placeholder="Descrie kitul, setările, avantajele și instrucțiunile..."
                  value={upDesc}
                  onChange={e => setUpDesc(e.target.value)}
                  className="w-full bg-black/60 border border-white/15 p-3 text-white outline-none resize-none"
                />
              </div>

              <div>
                <label className="block text-zinc-400 font-bold mb-1">Caracteristici Cheie (câte una pe linie)</label>
                <textarea
                  rows={2}
                  placeholder="Compatibil Windows 11&#10;FPS stabil la 240&#10;Include MasterServer"
                  value={upFeatures}
                  onChange={e => setUpFeatures(e.target.value)}
                  className="w-full bg-black/60 border border-white/15 p-3 text-white outline-none resize-none"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-white/10">
                <button type="button" onClick={() => setShowUploadModal(false)} className="tf-btn-dark px-4 py-2 text-xs">
                  <span>Anulează</span>
                </button>
                <button 
                  type="submit" 
                  disabled={savingResource || uploadingFile || (uploadMode === "file" && !upFileUrl)} 
                  className="tf-btn-primary px-6 py-2.5 text-xs font-black disabled:opacity-50"
                >
                  <span>
                    {savingResource ? "Se publică..." : "Publică Resursa Certificată"}
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* 4. MODALS (Edit & Security Report) */}
      {renderEditModal()}
      {renderSecurityReportModal()}

    </div>
  );
}
